/**
 * This code is based on the "use-methods" NPM module, with a change to rely on the "reinspect" module instead of the react "useReducer" module.
 * This enable support for time-travel-debugging inside the browser redux-devTool window
 */
import produce, { PatchListener } from 'immer';
import { Reducer, useMemo } from 'react';
import { useReducer } from 'reinspect';

export type StateAndCallbacksFor<M extends MethodsOrOptions> = [StateFor<M>, CallbacksFor<M>];

export type StateFor<M extends MethodsOrOptions> = M extends MethodsOrOptions<infer S, any>
  ? S
  : never;

export type CallbacksFor<M extends MethodsOrOptions> = M extends MethodsOrOptions<any, infer R>
  ? {
      [T in ActionUnion<R>['type']]: (
        ...payload: ActionByType<ActionUnion<R>, T>['payload']
      ) => void;
    }
  : never;

export type Methods<S = any, R extends MethodRecordBase<S> = any> = (state: S) => R;

export type Options<S = any, R extends MethodRecordBase<S> = any> = {
  methods: Methods<S, R>;
  patchListener?: PatchListener;
};

export type MethodsOrOptions<S = any, R extends MethodRecordBase<S> = any> =
  | Methods<S, R>
  | Options<S, R>;

export type MethodRecordBase<S = any> = Record<
  string,
  (...args: any[]) => S extends Record<string, unknown> ? S | void : S
>;

export type ActionUnion<R extends MethodRecordBase> = {
  [T in keyof R]: { type: T; payload: Parameters<R[T]> };
}[keyof R];

export type ActionByType<A, T> = A extends { type: infer T2 } ? (T extends T2 ? A : never) : never;

export default function useMethods<S, R extends MethodRecordBase<S>>(
  methodsOrOptions: MethodsOrOptions<S, R>,
  initialState: S,
  id?: string | number
): StateAndCallbacksFor<MethodsOrOptions<S, R>>;

// eslint-disable-next-line
export default function useMethods<S, R extends MethodRecordBase<S>, I>(
  methodsOrOptions: MethodsOrOptions<S, R>,
  initializerArg: I,
  initializer: (arg: I) => S,
  id?: string | number
): StateAndCallbacksFor<MethodsOrOptions<S, R>>;

// eslint-disable-next-line
export default function useMethods<S, R extends MethodRecordBase<S>>(
  methodsOrOptions: MethodsOrOptions<S, R>,
  initialState: any,
  initializer?: any,
  id?: string | number
): StateAndCallbacksFor<MethodsOrOptions<S, R>> {
  const [reducer, methodsFactory] = useMemo<[Reducer<S, ActionUnion<R>>, Methods<S, R>]>(() => {
    let methods: Methods<S, R>;
    let patchListener: PatchListener | undefined;
    if (typeof methodsOrOptions === 'function') {
      methods = methodsOrOptions;
    } else {
      methods = methodsOrOptions.methods;
      patchListener = methodsOrOptions.patchListener;
    }
    return [
      (state: S, action: ActionUnion<R>) => {
        return (produce as any)(
          state,
          (draft: S) => methods(draft)[action.type](...action.payload),
          patchListener
        );
      },
      methods
    ];
  }, [methodsOrOptions]);
  const [state, dispatch] = useReducer(reducer, initialState, initializer, id);
  const callbacks = useMemo(() => {
    const actionTypes: ActionUnion<R>['type'][] = Object.keys(methodsFactory(state));
    return actionTypes.reduce((accum, type) => {
      accum[type] = (...payload) => dispatch({ type, payload } as ActionUnion<R>);
      return accum;
    }, {} as CallbacksFor<typeof methodsFactory>);
    // TODO: RESEARCH
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [state, callbacks];
}
